"use client";

import { CSSProperties, ReactNode, useEffect, useRef, useState } from "react";

export type RevealVariant = "up" | "left" | "right" | "scale" | "wipe" | "depth";

type Props = {
  children: ReactNode;
  delay?: number;
  y?: number;
  x?: number;
  variant?: RevealVariant;
  className?: string;
};

/**
 * Lightweight scroll-reveal. Toggles a `.is-visible` class once the element
 * enters the viewport; the actual fade + drift live in globals.css (`.reveal`).
 * Kept deliberately subtle (small offsets) and dependency-free — no animation
 * library, so it adds ~nothing to the bundle. Honours prefers-reduced-motion.
 */
function offsets(variant: RevealVariant, x?: number, y?: number) {
  switch (variant) {
    case "left":
      return { x: x ?? -28, y: y ?? 0, scale: 1 };
    case "right":
      return { x: x ?? 28, y: y ?? 0, scale: 1 };
    case "scale":
      return { x: x ?? 0, y: y ?? 8, scale: 0.96 };
    case "depth":
      return { x: x ?? 0, y: y ?? 24, scale: 0.975 };
    case "wipe":
    case "up":
    default:
      return { x: x ?? 0, y: y ?? 22, scale: 1 };
  }
}

export default function Reveal({
  children,
  delay = 0,
  x,
  y,
  variant = "up",
  className,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (
      typeof window === "undefined" ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      !("IntersectionObserver" in window)
    ) {
      setVisible(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.12 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const o = offsets(variant, x, y);
  const style = {
    "--reveal-x": `${o.x}px`,
    "--reveal-y": `${o.y}px`,
    "--reveal-scale": String(o.scale),
    "--reveal-delay": `${delay}s`,
  } as CSSProperties;

  return (
    <div
      ref={ref}
      style={style}
      className={`reveal${visible ? " is-visible" : ""}${
        className ? ` ${className}` : ""
      }`}
    >
      {children}
    </div>
  );
}
